import { Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconCirclePlus, IconPencil, IconTrash } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { KIND_META, type TemplateKind } from './instagram-template-builders';

const KIND_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All types' },
  ...(Object.keys(KIND_META) as TemplateKind[]).map((k) => ({
    value: k,
    label: KIND_META[k].label,
  })),
];

function fmtDate(d: string | Date | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
}

/** Reusable Instagram DM templates — the "Instagram" tab of the Templates hub.
 *  Build/edit on the dedicated `new`/`edit` routes; fire them from the Instagram
 *  inbox composer. */
export function InstagramTemplatesPanel() {
  const utils = trpc.useUtils();
  const list = trpc.instagram.templates.list.useQuery({});
  const del = trpc.instagram.templates.delete.useMutation({
    onSuccess: () => {
      void utils.instagram.templates.list.invalidate();
      notifications.show({ message: 'Template deleted', color: 'green' });
      setConfirmDelete(null);
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const [filter, setFilter] = useState('');
  const [kindFilter, setKindFilter] = useState<string>('all');
  const [confirmDelete, setConfirmDelete] = useState<{ uid: string; name: string } | null>(null);

  const rows = useMemo(() => {
    const all = list.data ?? [];
    const f = filter.toLowerCase();
    return all.filter((t) => {
      if (f && !t.name.toLowerCase().includes(f)) return false;
      if (kindFilter !== 'all' && t.kind !== kindFilter) return false;
      return true;
    });
  }, [list.data, filter, kindFilter]);

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Reusable message templates you can fire from the Instagram inbox composer.
          </Text>
          <Button
            component={Link}
            to="/vendor/instagram/templates/new"
            leftSection={<IconCirclePlus size={14} />}
          >
            New template
          </Button>
        </Group>
        <Group>
          <TextInput
            placeholder="Filter by name…"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            data={KIND_FILTERS}
            value={kindFilter}
            onChange={(v) => setKindFilter(v ?? 'all')}
            aria-label="Filter by type"
          />
        </Group>
        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={240} m="md" />
          ) : list.isError ? (
            <Text c="red" p="md">
              {list.error.message}
            </Text>
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Scope</Table.Th>
                  <Table.Th>Updated</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((t) => {
                  const meta = KIND_META[t.kind as TemplateKind] ?? KIND_META.quick_replies;
                  return (
                    <Table.Tr key={t.uid}>
                      <Table.Td>
                        <Anchor
                          renderRoot={(props) => (
                            <Link
                              to="/vendor/instagram/templates/$uid/edit"
                              params={{ uid: t.uid }}
                              {...props}
                            />
                          )}
                        >
                          <Text size="sm">{t.name}</Text>
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={meta.color}
                          variant="light"
                          leftSection={<meta.Icon size={12} />}
                        >
                          {meta.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {t.instagramAccountId
                            ? `Account #${t.instagramAccountId}`
                            : 'All accounts'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {fmtDate(t.updatedAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td align="right">
                        <Group gap={4} justify="flex-end">
                          <ActionIcon
                            variant="subtle"
                            aria-label="Edit"
                            renderRoot={(props) => (
                              <Link
                                to="/vendor/instagram/templates/$uid/edit"
                                params={{ uid: t.uid }}
                                {...props}
                              />
                            )}
                          >
                            <IconPencil size={14} />
                          </ActionIcon>
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            loading={del.isPending && del.variables?.uid === t.uid}
                            onClick={() => setConfirmDelete({ uid: t.uid, name: t.name })}
                            aria-label="Delete"
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="md">
                        {(list.data ?? []).length === 0
                          ? 'No templates yet. Build one to reuse it from the composer.'
                          : 'No templates match the current filters.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete template?"
        centered
      >
        <Stack>
          <Text size="sm">
            This permanently deletes{' '}
            <Text span fw={600}>
              {confirmDelete?.name}
            </Text>
            . It will no longer appear in the Instagram composer. This cannot be undone.
          </Text>
          <Group justify="end">
            <Button variant="default" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={del.isPending}
              onClick={() => confirmDelete && del.mutate({ uid: confirmDelete.uid })}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
